import React, {useState, useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {PaymentMethodRow} from './PaymentMethodRow'
import {CommissionWalletRow} from './CommissionWalletRow'
import {PaymentMethodSelectorProps, PaymentMethodOption} from './types'

const BACKEND_API_V2 = process.env.REACT_APP_BACKEND_API_V2 ?? ''

function detectSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /^((?!chrome|android).)*safari/i.test(ua)
}

function detectChrome(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent
  return /chrome/i.test(ua) && !/edg|opr|opera/i.test(ua)
}

/**
 * Strict primary display order (by method_id). These are shown up-front.
 * Google Pay / Apple Pay share slot 4 — the backend returns only the one the
 * browser supports, so at most one of them ever renders.
 */
const PRIMARY_ORDER: string[] = [
  'micropayment_sofort', // 1. Onlineüberweisung (1% Rabatt)
  'creditcard_2', // 2. Credit Card
  'stripe_other_cards', // 3. Amex / UnionPay / JCB
  'airwallex_googlepay', // 4. Google Pay
  'airwallex_applepay', // 4. Apple Pay
  'bpay', // 5. Binance Pay
  'micropayment_prepay', // 6. Manual Bank Transfer
]

/** Methods pinned to the very bottom of the expandable "additional" group. */
const LAST_IN_ADDITIONAL: string[] = ['klarna']

const primaryRank = (id: string): number => {
  const i = PRIMARY_ORDER.indexOf(id)
  return i === -1 ? Number.POSITIVE_INFINITY : i
}

/**
 * Shared payment method selector used across all checkout flows.
 *
 * Two modes:
 * 1. API-driven (default): fetches methods from GET /payment/methods
 * 2. Pre-fetched: pass `methods` prop directly (for testing or SSR)
 *
 * Frontend is a dumb renderer — all filtering happens server-side.
 * Checkout logic (API calls, provider routing) stays in parent components.
 */
export function PaymentMethodSelector({
  methods: methodsProp,
  apiBaseUrl,
  countryId,
  amountEur,
  trustlyAvailable,
  selectedMethod,
  onMethodChange,
  walletMode,
  commissionWalletBalance = 0,
  useCommissionWallet = false,
  onCommissionWalletChange,
}: PaymentMethodSelectorProps) {
  const {t} = useTranslation()
  const [methods, setMethods] = useState<PaymentMethodOption[]>(methodsProp ?? [])
  const [loading, setLoading] = useState(!methodsProp)
  const [error, setError] = useState<string | null>(null)
  const [showAdditional, setShowAdditional] = useState(false)

  useEffect(() => {
    // Skip fetch if methods were provided as prop
    if (methodsProp) {
      setMethods(methodsProp)
      setLoading(false)
      return
    }

    const fetchMethods = async () => {
      try {
        setLoading(true)
        setError(null)

        const base = apiBaseUrl || BACKEND_API_V2
        const params = new URLSearchParams()
        if (countryId && countryId > 0) params.set('country_id', String(countryId))
        if (amountEur && amountEur > 0) params.set('amount_eur', String(amountEur))
        if (detectSafari()) params.set('is_safari', 'true')
        if (detectChrome()) params.set('is_chrome', 'true')

        const token = localStorage.getItem('token') ?? ''
        const response = await fetch(`${base}/payment/methods?${params}`, {
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': token,
          },
        })

        const data = await response.json()
        if (data.methods) {
          setMethods(data.methods)
        } else {
          setError('Failed to load payment methods')
        }
      } catch (e) {
        console.error('Failed to fetch payment methods:', e)
        setError('Payment methods unavailable')
      } finally {
        setLoading(false)
      }
    }

    fetchMethods()
  }, [methodsProp, apiBaseUrl, countryId, amountEur, trustlyAvailable])

  if (loading) {
    return (
      <div id='kt_create_new_payment_method'>
        <div className='py-5 text-center text-muted'>
          Loading payment methods...
        </div>
      </div>
    )
  }

  if (error || methods.length === 0) {
    return (
      <div id='kt_create_new_payment_method'>
        <div className='py-5 text-center text-muted'>
          {error ?? 'No payment methods available'}
        </div>
      </div>
    )
  }

  // Split into the strict primary group (shown up-front) and the rest (accordion).
  const primary = methods
    .filter((m) => PRIMARY_ORDER.includes(m.method_id))
    .sort((a, b) => primaryRank(a.method_id) - primaryRank(b.method_id))

  const additional = methods
    .filter((m) => !PRIMARY_ORDER.includes(m.method_id))
    .sort((a, b) => {
      const al = LAST_IN_ADDITIONAL.includes(a.method_id)
      const bl = LAST_IN_ADDITIONAL.includes(b.method_id)
      if (al !== bl) return al ? 1 : -1 // klarna (and any pinned) go last
      return (a.display_order ?? 0) - (b.display_order ?? 0)
    })

  // Keep the accordion open if the current selection lives inside it.
  const selectedInAdditional = additional.some((m) => m.method_id === selectedMethod)
  const additionalOpen = showAdditional || selectedInAdditional

  const renderRow = (method: PaymentMethodOption) => (
    <PaymentMethodRow
      key={method.method_id}
      method={method}
      isSelected={selectedMethod === method.method_id}
      onSelect={() => onMethodChange(method.method_id)}
    />
  )

  return (
    <div id='kt_create_new_payment_method'>
      {primary.map(renderRow)}

      {additional.length > 0 && (
        <>
          <button
            type='button'
            className='btn btn-sm btn-light-primary w-100 my-3 d-flex align-items-center justify-content-center'
            onClick={() => setShowAdditional((v) => !v)}
            aria-expanded={additionalOpen}
          >
            <span className='me-2'>
              {additionalOpen
                ? t('PAYMENT_METHOD_SHOW_LESS')
                : t('PAYMENT_METHOD_SHOW_MORE', {count: additional.length})}
            </span>
            <i className={`bi ${additionalOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-7`}></i>
          </button>

          {additionalOpen && <div>{additional.map(renderRow)}</div>}
        </>
      )}

      {walletMode === 'checkbox' && (
        <CommissionWalletRow
          balance={commissionWalletBalance}
          checked={useCommissionWallet}
          onChange={onCommissionWalletChange ?? (() => {})}
        />
      )}
    </div>
  )
}
